import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { selectLandingType, selectShowCheckout } from '../../../store/plans';

export const PlanList = styled.ul`
	display: flex;
	flex-direction: column;
	align-items: center;
	width: 100%;
	margin: 24px 0 16px;
	list-style: none;
`;

export const PlanItem = styled.li`
	width: 100%;
	&:not(:last-child) {
		margin-bottom: 40px;
	}
`;

export const PlanTitle = styled.h3`
	text-align: left;
	color: ${(props) => props.theme.colors.colorText};
	font-size: ${(props) => props.theme.fontSizes.font20};
	font-weight: ${(props) => props.theme.fontWeights.bold};
	letter-spacing: -0.1px;
	text-transform: uppercase;
	margin-bottom: 16px;
`;

export const Separator = styled.div`
	width: 100%;
	min-width: 100%;
	min-height: 1px;
	height: 1px;
	background-color: ${(props) => props.theme.colors.colorPlanSeparator};
`;

export const PlanWrapper = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
	min-height: 54px;
`;

export const PlanName = styled.div`
	color: ${(props) => props.theme.colors.colorText};
	font-size: ${(props) => props.theme.fontSizes.font16};
	font-weight: ${(props) => props.theme.fontWeights.light};
	line-height: ${(props) => props.theme.lineHeights.lineHeight22};
	white-space: pre-line;
	text-align: left;
`;

export const PlanPricesWrapper = styled.div`
	display: flex;
	flex-direction: ${({ isRow }) => (isRow ? 'row' : 'column')};
	align-items: ${({ isRow }) => (isRow ? 'center' : 'flex-end')};
	gap: ${({ isRow }) => (isRow ? '5px' : '2px')};
`;

export const PlanPrice = styled.div`
	color: ${(props) => props.theme.colors.colorText};
	font-size: ${(props) => props.theme.fontSizes.font16};
	font-weight: ${(props) => props.theme.fontWeights.bold};
	letter-spacing: -0.2px;
`;

const BeforePrice = styled.span`
	text-decoration: line-through;
	color: ${(props) => props.theme.colors.colorDomain};
	font-size: ${(props) => props.theme.fontSizes.font12};
	font-weight: ${(props) => props.theme.fontWeights.extraLight};
	letter-spacing: -0.2px;
`;

const WeekPrice = styled(BeforePrice)`
	text-decoration: none;
	font-size: ${(props) => props.theme.fontSizes.font14};
	color: ${(props) => props.theme.colors.colorText};
`;

const PlanDisclaimer = styled.div`
	margin-top: 16px;
	text-align: left;
	color: ${(props) => props.theme.colors.colorText};
	font-size: ${(props) => props.theme.fontSizes.font14};
	font-weight: ${(props) => props.theme.fontWeights.extraLight};
	line-height: ${(props) => props.theme.lineHeights.lineHeight20};
	letter-spacing: -0.461px;
	${({ extra }) =>
		extra &&
		css`
			background-color: ${(props) => props.theme.colors.colorHybridPlanBg};
			padding: 8px 16px;
			text-align: center;
		`}
`;

const DisclaimerText = styled.span`
	& > span {
		cursor: pointer;
		text-decoration: underline;
	}
`;

const HybridPlan = () => {
	const { t } = useTranslation();
	const showCheckout = useSelector(selectShowCheckout);
	const landingType = useSelector(selectLandingType);
	const isExpensive = landingType === 'trial_exp';
	const planOptions = useMemo(() => {
		return [
			{
				id: 1,
				title: t('price_today'),
				planName: t('7_day_trial'),
				oldPrice: `${t('before')} $${isExpensive ? '19.99' : '9.99'}`,
				price: '$0.99*',
				planDisclaimer: t('price_disclaimer'),
			},
			{
				id: 2,
				title: t('price_after_trial'),
				planName: `12-${t('week')} ${t('plan')}`,
				pricePerWeek: `${t('only')} $${isExpensive ? '6.66' : '4.16'}/${t(
					'week'
				)}`,
				planDisclaimer: showCheckout ? t('price_trial_disclaimer') : t('hybrid_disclaimer'),
			},
		];
	}, [t, isExpensive, showCheckout]);

	const onLinkClick = ({ target: { id } }) => {
		const termsLink = 'https://family-locator.com/terms-of-use-info/';
		const links = {
			privacy: 'https://family-locator.com/privacy-policy-info/',
			terms: termsLink,
			refund: termsLink,
			'terms-of-use': termsLink,
			'fl-link': 'https://family-locator.com',
		};

		if (id && links[id]) window.open(links[id]);
	};

	const renderPlanOption = (plan, index) => {
		const extraDisclaimerStyles = plan.id === 1 && showCheckout;
		return (
			<PlanItem key={index} id={'plan-button-' + (index + 1)}>
				<PlanTitle>{plan.title}</PlanTitle>
				<Separator />
				<PlanWrapper>
					<PlanName>{plan.planName}</PlanName>
					<PlanPricesWrapper isRow={plan.oldPrice}>
						{plan.oldPrice && !showCheckout && (
							<BeforePrice>{plan.oldPrice}</BeforePrice>
						)}
						{plan.price && <PlanPrice>{plan.price}</PlanPrice>}
						{plan.pricePerWeek && <WeekPrice>{plan.pricePerWeek}</WeekPrice>}
					</PlanPricesWrapper>
				</PlanWrapper>
				<Separator />
				<PlanDisclaimer extra={extraDisclaimerStyles}>
					{extraDisclaimerStyles ? '⚡️' : '*'}
					<DisclaimerText
						dangerouslySetInnerHTML={{
							__html: t(plan.planDisclaimer, {
								fullPrice: isExpensive ? '$79.99' : '$49.99',
							}),
						}}
						onClick={(e) => onLinkClick(e)}
					/>
					{extraDisclaimerStyles && '⚡️'}
				</PlanDisclaimer>
			</PlanItem>
		);
	};
	return <PlanList>{planOptions.map(renderPlanOption)}</PlanList>;
};

export default HybridPlan;
